/**
 * Authentication API client
 */

import { apiFetch, authenticatedFetch } from './client';
import type {
  TokenDto,
  GoogleOAuthTokenDto,
  JwtPayload,
  SmartLinkOnboardingResponse,
  SmartLinkAccountExistsResponse,
} from './types';

export interface LoginRequest {
  email: string;
  password: string;
  /** Pending org invite token (from accept-invite link) */
  invite_token?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  /** Pending org invite token (from accept-invite link) */
  invite_token?: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  email: string;
  token: string;
  password: string;
}

export interface GoogleOAuthCallbackRequest {
  code: string;
  redirect_uri?: string;
}

/**
 * Login with email and password. May return a Smart Link onboarding response
 * instead of tokens — see isSmartLinkOnboardingResponse.
 */
export async function login(
  data: LoginRequest,
): Promise<TokenDto | SmartLinkOnboardingResponse> {
  return apiFetch<TokenDto | SmartLinkOnboardingResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Register a new user. May return a "Smart account exists" response instead
 * of tokens when the email has an unlinked Smart Link claim — see
 * isSmartLinkAccountExistsResponse. The user logs in instead (which verifies
 * their existing Smart password and offers onboarding) rather than choosing
 * a new password here, so the two systems' passwords never diverge.
 */
export async function register(
  data: RegisterRequest,
): Promise<TokenDto | SmartLinkAccountExistsResponse> {
  return apiFetch<TokenDto | SmartLinkAccountExistsResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/** Finish Smart Link onboarding (from login or register) using the ticket. */
export async function confirmSmartLinkOnboarding(ticket: string): Promise<TokenDto> {
  return apiFetch<TokenDto>('/auth/smart-link/confirm', {
    method: 'POST',
    body: JSON.stringify({ ticket }),
  });
}

/**
 * Refresh access token
 */
export async function refreshToken(
  refreshToken: string,
): Promise<TokenDto> {
  return apiFetch<TokenDto>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}

/**
 * Logout (invalidate refresh token)
 */
export async function logout(refreshToken: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
}

/**
 * Verify token and get user payload
 */
export async function verifyToken(
  accessToken: string,
): Promise<JwtPayload> {
  return authenticatedFetch<JwtPayload>('/auth/verify-token', accessToken, {
    method: 'POST',
  });
}

/**
 * Request password reset
 */
export async function requestPasswordReset(
  data: PasswordResetRequest,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/password-reset', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Verify email with the code sent to the user (e.g. from signup verification email)
 */
export async function verifyEmail(
  email: string,
  code: string,
  inviteToken?: string,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify({
      email,
      code,
      ...(inviteToken?.trim() ? { invite_token: inviteToken.trim() } : {}),
    }),
  });
}

/**
 * Resend email verification code for the authenticated user.
 */
export async function requestEmailVerification(
  accessToken: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    '/auth/request-email-verification',
    accessToken,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

/**
 * Verify email using a pending org invite (inbox proven by invite link).
 */
export async function verifyEmailViaInvite(
  accessToken: string,
  inviteToken: string,
): Promise<{ message: string; access_token?: string }> {
  return authenticatedFetch<{ message: string; access_token?: string }>(
    '/auth/verify-email-via-invite',
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({ invite_token: inviteToken }),
    },
  );
}

/**
 * Confirm password reset (also returns session tokens for auto-login)
 */
export async function confirmPasswordReset(
  data: PasswordResetConfirmRequest,
): Promise<{ message: string } & Partial<TokenDto>> {
  return apiFetch<{ message: string } & Partial<TokenDto>>('/auth/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Get Google OAuth URL
 * @param redirectUri - Frontend origin (e.g. window.location.origin); must be in GOOGLE_REDIRECT_URIS
 */
export async function getGoogleAuthUrl(
  redirectUri?: string,
): Promise<{ url: string }> {
  const url =
    redirectUri != null
      ? `/auth/google?redirect_uri=${encodeURIComponent(redirectUri)}`
      : '/auth/google';
  return apiFetch<{ url: string }>(url);
}

/**
 * Handle Google OAuth callback
 */
export async function handleGoogleOAuthCallback(
  data: GoogleOAuthCallbackRequest,
): Promise<GoogleOAuthTokenDto> {
  return apiFetch<GoogleOAuthTokenDto>('/auth/google/callback', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Check if user has permission
 */
export async function checkPermission(
  accessToken: string,
  permission: string,
): Promise<{ hasPermission: boolean }> {
  return authenticatedFetch<{ hasPermission: boolean }>(
    `/auth/check-permission?permission=${encodeURIComponent(permission)}`,
    accessToken,
    {
      method: 'GET',
    },
  );
}

/**
 * List all server users (requires user:read). Use an admin's access token.
 */
export interface ServerUserSummary {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
  phone?: string | null;
  countryCode?: string | null;
  googleId?: string | null;
  isEmailVerified?: boolean;
  isPhoneVerified?: boolean;
  emailVerifiedAt?: string | null;
  email_verified_at?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export async function listServerUsers(
  accessToken: string,
): Promise<{ users: ServerUserSummary[] }> {
  return authenticatedFetch<{ users: ServerUserSummary[] }>(
    '/users',
    accessToken,
    { method: 'GET' },
  );
}

/**
 * List all organizations (requires organization:list). Admin token.
 */
export interface ServerOrganizationSummary {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  type?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  addressSecondary?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  timezone?: string | null;
  parentOrganizationId?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export async function listServerOrganizations(
  accessToken: string,
): Promise<{ organizations: ServerOrganizationSummary[] }> {
  return authenticatedFetch<{ organizations: ServerOrganizationSummary[] }>(
    '/organizations/all',
    accessToken,
    { method: 'GET' },
  );
}

export async function createServerOrganization(
  accessToken: string,
  body: {
    name: string;
    slug: string;
    description?: string;
    type?: string;
  },
): Promise<ServerOrganizationSummary> {
  return authenticatedFetch<ServerOrganizationSummary>(
    '/organizations',
    accessToken,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/** Full organization row (GET /organizations/:id). Use includeDeleted with organization:list for soft-deleted rows. */
export async function getServerOrganization(
  accessToken: string,
  organizationId: string,
  opts?: { includeDeleted?: boolean },
): Promise<Record<string, unknown>> {
  const q =
    opts?.includeDeleted === true ? '?includeDeleted=1' : '';
  return authenticatedFetch<Record<string, unknown>>(
    `/organizations/${organizationId}${q}`,
    accessToken,
    { method: 'GET' },
  );
}

export type UpdateServerOrganizationBody = {
  name?: string;
  slug?: string;
  description?: string | null;
  type?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  addressSecondary?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;
  timezone?: string | null;
  parentOrganizationId?: string | null;
};

export async function updateServerOrganization(
  accessToken: string,
  organizationId: string,
  body: UpdateServerOrganizationBody,
): Promise<ServerOrganizationSummary> {
  return authenticatedFetch<ServerOrganizationSummary>(
    `/organizations/${organizationId}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function softDeleteServerOrganization(
  accessToken: string,
  organizationId: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    `/organizations/${organizationId}/soft-delete`,
    accessToken,
    { method: 'POST' },
  );
}

export async function undeleteServerOrganization(
  accessToken: string,
  organizationId: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    `/organizations/${organizationId}/undelete`,
    accessToken,
    { method: 'POST' },
  );
}

export async function hardDeleteServerOrganization(
  accessToken: string,
  organizationId: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    `/organizations/${organizationId}/hard-delete`,
    accessToken,
    { method: 'POST' },
  );
}

/**
 * Get a single user by ID (requires user:read). Use an admin's access token.
 */
export async function getServerUser(
  accessToken: string,
  userId: string,
): Promise<Record<string, unknown>> {
  return authenticatedFetch<Record<string, unknown>>(
    `/users/${userId}`,
    accessToken,
    { method: 'GET' },
  );
}

/**
 * List all roles (requires role:read). Use an admin's access token.
 */
export interface RoleSummary {
  id: string;
  name: string;
}

export async function listRoles(
  accessToken: string,
): Promise<RoleSummary[]> {
  return authenticatedFetch<RoleSummary[]>('/roles', accessToken, {
    method: 'GET',
  });
}

/**
 * List all permissions (requires permission:read). Use an admin's access token.
 */
export interface PermissionSummary {
  id: string;
  name: string;
  description?: string | null;
  resource?: string | null;
  action?: string | null;
  identifier?: string | null;
}

/**
 * List all permission definitions (requires permission:read).
 * Uses GET /permissions (canonical); same payload as legacy GET /roles/permissions.
 */
export async function listPermissions(
  accessToken: string,
): Promise<PermissionSummary[]> {
  return authenticatedFetch<PermissionSummary[]>('/permissions', accessToken, {
    method: 'GET',
  });
}

/**
 * Update a user's roles (requires user:update). Use an admin's access token.
 */
export async function updateServerUserRoles(
  accessToken: string,
  userId: string,
  roleIds: string[],
): Promise<Record<string, unknown>> {
  return authenticatedFetch<Record<string, unknown>>(
    `/roles/users/${userId}/roles`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ roleIds }) },
  );
}

/**
 * Update a user's direct permissions (requires user:update). Use an admin's access token.
 */
export async function updateServerUserPermissions(
  accessToken: string,
  userId: string,
  permissionIds: string[],
): Promise<Record<string, unknown>> {
  return authenticatedFetch<Record<string, unknown>>(
    `/roles/users/${userId}/permissions`,
    accessToken,
    { method: 'POST', body: JSON.stringify({ permissionIds }) },
  );
}

/**
 * Soft-delete a user (requires user:delete). Use an admin's access token.
 */
export async function softDeleteUser(
  accessToken: string,
  userId: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    `/users/${userId}/soft-delete`,
    accessToken,
    { method: 'POST' },
  );
}

/**
 * Permanently delete a user (requires user:hard-delete). Use a super-admin access token.
 */
export async function hardDeleteUser(
  accessToken: string,
  userId: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    `/users/${userId}/hard-delete`,
    accessToken,
    { method: 'POST' },
  );
}

/**
 * Restore a soft-deleted user and send reclaim email (requires user:undelete). Use an admin's access token.
 */
export async function undeleteUser(
  accessToken: string,
  userId: string,
): Promise<{ message: string; resetToken?: string }> {
  return authenticatedFetch<{ message: string; resetToken?: string }>(
    `/users/${userId}/undelete`,
    accessToken,
    { method: 'POST' },
  );
}

/**
 * Update current user profile (PATCH /users/me). Use the user's access token.
 */
export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  avatarUrl?: string | null;
  phone?: string;
  countryCode?: string;
}

export async function updateMyProfile(
  accessToken: string,
  data: UpdateProfileRequest,
): Promise<Record<string, unknown>> {
  return authenticatedFetch<Record<string, unknown>>('/users/me', accessToken, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

/**
 * List active sessions/devices for the current user.
 * Uses the user's own access token.
 */
export interface SessionSummary {
  id: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  authMethod?: string | null;
  userAgent?: string | null;
  lastUsedAt?: string | null;
}

export async function listMySessions(
  accessToken: string,
): Promise<{ sessions: SessionSummary[] }> {
  return authenticatedFetch<{ sessions: SessionSummary[] }>(
    '/users/me/sessions',
    accessToken,
    { method: 'GET' },
  );
}

/**
 * Revoke (log out) a single refresh-token session for the current user.
 */
export async function revokeMySession(
  accessToken: string,
  sessionId: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    `/users/me/sessions/${sessionId}/revoke`,
    accessToken,
    { method: 'POST' },
  );
}

/**
 * List active sessions/devices for a specific user (admin).
 * Use an admin access token.
 */
export async function listServerUserSessions(
  accessToken: string,
  userId: string,
): Promise<{ sessions: SessionSummary[] }> {
  return authenticatedFetch<{ sessions: SessionSummary[] }>(
    `/users/${userId}/sessions`,
    accessToken,
    { method: 'GET' },
  );
}

/**
 * Revoke (log out) a specific session/devices for a server user (admin).
 */
export async function revokeServerUserSession(
  accessToken: string,
  userId: string,
  sessionId: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    `/users/${userId}/sessions/${sessionId}/revoke`,
    accessToken,
    { method: 'POST' },
  );
}

/**
 * Update a user's profile (PATCH /users/:id). Requires user:update. Use an admin's access token.
 */
export async function updateServerUserProfile(
  accessToken: string,
  userId: string,
  data: UpdateProfileRequest,
): Promise<Record<string, unknown>> {
  return authenticatedFetch<Record<string, unknown>>(
    `/users/${userId}`,
    accessToken,
    { method: 'PATCH', body: JSON.stringify(data) },
  );
}

/** Dev-only: captured email entry (when EMAIL_DEV_CAPTURE is enabled). */
export interface CapturedEmail {
  to: string;
  subject: string;
  text?: string;
  html?: string;
  meta?: Record<string, unknown>;
  sentAt: string;
}

/**
 * Dev-only: list captured emails (GET /api/dev/captured-emails). Optional filter by email.
 */
export async function getCapturedEmails(
  email?: string,
): Promise<{ emails: CapturedEmail[] }> {
  const q = email ? `?email=${encodeURIComponent(email)}` : '';
  return apiFetch<{ emails: CapturedEmail[] }>(`/dev/captured-emails${q}`);
}

/**
 * Dev-only: clear captured emails (DELETE /api/dev/captured-emails). Optional filter by email.
 */
export async function clearCapturedEmails(
  email?: string,
): Promise<{ cleared: number }> {
  const q = email ? `?email=${encodeURIComponent(email)}` : '';
  return apiFetch<{ cleared: number }>(`/dev/captured-emails${q}`, {
    method: 'DELETE',
  });
}

/** Dev-only: captured SMS entry (when SMS_DEV_CAPTURE or Pinpoint not configured). */
export interface CapturedSms {
  to: string;
  body: string;
  meta?: Record<string, unknown>;
  sentAt: string;
}

/**
 * Dev-only: list captured SMS (GET /api/dev/captured-sms). Optional filter by phone.
 */
export async function getCapturedSms(
  phone?: string,
): Promise<{ sms: CapturedSms[] }> {
  const q = phone ? `?phone=${encodeURIComponent(phone)}` : '';
  return apiFetch<{ sms: CapturedSms[] }>(`/dev/captured-sms${q}`);
}

/**
 * Dev-only: clear captured SMS (DELETE /api/dev/captured-sms). Optional filter by phone.
 */
export async function clearCapturedSms(
  phone?: string,
): Promise<{ cleared: number }> {
  const q = phone ? `?phone=${encodeURIComponent(phone)}` : '';
  return apiFetch<{ cleared: number }>(`/dev/captured-sms${q}`, {
    method: 'DELETE',
  });
}

/**
 * Request phone verification code. Sends SMS to current user's phone. Requires auth.
 */
export async function requestPhoneVerification(
  accessToken: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    '/auth/request-phone-verification',
    accessToken,
    { method: 'POST', body: JSON.stringify({}) },
  );
}

/**
 * Verify phone with code (unauthenticated - looks up user by phone).
 */
export async function verifyPhone(
  phone: string,
  code: string,
): Promise<{ message: string }> {
  return apiFetch<{ message: string }>('/auth/verify-phone', {
    method: 'POST',
    body: JSON.stringify({ phone, code }),
  });
}

/**
 * Verify phone with code for the authenticated user.
 * Use when the user has requested a code and is verifying with their token.
 */
export async function verifyPhoneAsUser(
  accessToken: string,
  code: string,
): Promise<{ message: string }> {
  return authenticatedFetch<{ message: string }>(
    '/auth/verify-phone',
    accessToken,
    {
      method: 'POST',
      body: JSON.stringify({ code }),
    },
  );
}


